/* eslint-disable @typescript-eslint/no-shadow */
import { Disclosure, Popover, Transition } from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useLockedBody } from 'usehooks-ts';

import Container from '@/components/elements/Container';
import Heading from '@/components/elements/Heading';
import StoryblokLink from '@/components/elements/StoryblokLink';
import { storyblokLink } from '@/helpers/storyblokLink';

import DynamicDropdowns from './Dropdowns';

function NavMobile({ main, utility, dark = false }) {
  const [locked] = useState(false);

  useLockedBody(locked, 'root');

  return (
    <Popover className="flex items-center justify-end lg:hidden">
      <Popover.Button
        className={twMerge(
          'flex aspect-square items-center justify-center lg:hidden',
          dark ? 'text-white' : 'text-theme-black'
        )}
      >
        <span className="sr-only">Menu</span>
        <svg
          className="size-8"
          viewBox="0 0 34 34"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.761194 6C0.340798 6 0 6.33579 0 6.75C0 7.16421 0.340798 7.5 0.761194 7.5H33.2388C33.6592 7.5 34 7.16421 34 6.75C34 6.33579 33.6592 6 33.2388 6H0.761194ZM0 16.5961C0 16.1819 0.340798 15.8461 0.761194 15.8461H33.2388C33.6592 15.8461 34 16.1819 34 16.5961C34 17.0103 33.6592 17.3461 33.2388 17.3461H0.761194C0.340798 17.3461 0 17.0103 0 16.5961ZM0 26.4423C0 26.0281 0.340798 25.6923 0.761194 25.6923H33.2388C33.6592 25.6923 34 26.0281 34 26.4423C34 26.8565 33.6592 27.1923 33.2388 27.1923H0.761194C0.340798 27.1923 0 26.8565 0 26.4423Z"
            fill="currentColor"
          />
        </svg>
      </Popover.Button>
      <Transition
        className="absolute left-0 top-0 z-20 h-[calc(100vh-var(--notification-height))] w-full overscroll-contain"
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-90"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-90"
      >
        <Popover.Overlay
          className={twMerge(
            'fixed inset-0',
            dark ? 'bg-theme-black/50' : 'bg-white/50'
          )}
        />
        <Popover.Panel className="absolute inset-0 transition lg:hidden">
          <div
            className={twMerge(
              'absolute inset-0 md:inset-12 ring-1 ring-white/20',
              dark ? 'bg-theme-gray-medium' : 'bg-theme-offwhite'
            )}
          >
            <div className="flex h-full flex-col">
              <div
                className={twMerge(
                  'absolute inset-x-0 top-0 z-50 flex shrink-0 items-center justify-between p-4 backdrop-blur-sm',
                  dark ? 'bg-theme-gray-medium/80' : 'bg-theme-offwhite/80'
                )}
              >
                <div></div>
                <div className="">
                  <Popover.Button
                    className={twMerge(
                      'flex aspect-square items-center justify-center p-2',
                      dark ? 'text-white' : 'text-theme-gray-medium'
                    )}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon
                      className="inline-block size-6"
                      aria-hidden="true"
                    />
                  </Popover.Button>
                </div>
              </div>
              <div className="flex flex-1 flex-col overflow-auto overscroll-contain pb-6 pt-16">
                <div className="flex-1">
                  <Container size="full">
                    {main?.map((item) => (
                      <div key={item._uid}>
                        {item.dropdown?.length ? (
                          <Disclosure>
                            {({ open }) => (
                              <>
                                <Disclosure.Button
                                  className={twMerge(
                                    'group flex w-full items-center justify-between py-1 border-b-2 border-theme-grey/20',
                                    dark
                                      ? 'text-white'
                                      : 'text-theme-gray-medium'
                                  )}
                                >
                                  <Heading
                                    size="3xs"
                                    weight="semi"
                                    element="span"
                                    className="whitespace-nowrap"
                                  >
                                    {item?.label ? item.label : item.name}
                                  </Heading>
                                  <div className="flex aspect-square items-center justify-center p-3">
                                    <ChevronDownIcon
                                      className={twMerge(
                                        'text-neutral-400 h-5 w-5',
                                        open ? 'rotate-180' : ''
                                      )}
                                      aria-hidden="true"
                                    />
                                  </div>
                                </Disclosure.Button>
                                <Transition
                                  enter="transition ease-out duration-200"
                                  enterFrom="opacity-0 translate-y-1"
                                  enterTo="opacity-100 translate-y-0"
                                  leave="transition ease-in duration-150"
                                  leaveFrom="opacity-100 translate-y-0"
                                  leaveTo="opacity-0 translate-y-1"
                                >
                                  <Disclosure.Panel className="">
                                    <div className="pb-8 pt-2">
                                      <div className="relative grid grid-cols-1 gap-y-8">
                                        {item.dropdown?.map((dropdown) => (
                                          <DynamicDropdowns
                                            key={dropdown._uid}
                                            dropdown={dropdown}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  </Disclosure.Panel>
                                </Transition>
                              </>
                            )}
                          </Disclosure>
                        ) : (
                          <>
                            {item.page ? (
                              <StoryblokLink link={item.page}>
                                <span className="text-md group block w-full items-center justify-between py-4 font-semibold text-theme-black dark:text-theme-white">
                                  {item?.label ? item.label : item.name}
                                </span>
                              </StoryblokLink>
                            ) : (
                              <span className="text-md group block w-full items-center justify-between py-4 font-semibold text-theme-black dark:text-theme-white">
                                {item?.label ? item.label : item.name}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    <div className="mt-8 flex flex-col gap-6">
                      {utility?.map((item) => (
                        <div key={item._uid} className="relative">
                          <Link
                            href={storyblokLink(item.page)}
                            className={twMerge(
                              'group inline w-full items-center justify-between py-3 text-md font-normal',
                              dark ? 'text-white' : 'text-theme-gray-medium'
                            )}
                          >
                            <>{item?.label ? item.label : item.name}</>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </Container>
                </div>
              </div>
            </div>
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
export default NavMobile;
